import InvoiceIcon from 'assets/svg/profile/invoice.svg'
import LogoutIcon from 'assets/svg/profile/logout.svg'
import OrderIcon from 'assets/svg/profile/order.svg'
import RefundIcon from 'assets/svg/profile/refund.svg'
import ReviewIcon from 'assets/svg/profile/review.svg'
import TenderIcon from 'assets/svg/profile/tender.svg'
import Flex from 'common/components/Flex'
import Image from 'common/components/Image'
import MenuPin from 'common/components/Profile/MenuPin'
import MenuPoints from 'common/components/Profile/MenuPoints'
import MenuVoucher from 'common/components/Profile/MenuVoucher'
import MenuWallet from 'common/components/Profile/MenuWallet'
import Skeleton from 'common/components/Skeleton'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import { useStoreActions } from 'easy-peasy'
import cdn from 'lib/cdn'
import { useLoadedFlag } from 'lib/feature-flags'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import IStoreModel from 'stores/models/interface'
import styled from 'styled-components'

const MenuCreditV2 = dynamic(
  () => import('common/components/Profile/MenuCreditV2'),
  { ssr: false },
)

const MenuCredit = dynamic(
  () => import('common/components/Profile/MenuCredit'),
  { ssr: false },
)

const StyledProfile = styled.div`
  width: 480px;
`
const ProfilePictureContainer = styled(Flex)`
  border-bottom: solid 1px ${theme.colors.grayLight};
  padding: 10px 20px;
  flex-shrink: 0;
`
const ProfileLink = styled.a`
  ${theme.typography.bh6}
  color: ${theme.colors.black};
  margin-left: 12px;
  align-self: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const ProfileOverviewContainer = styled.div`
  width: 100%;
  padding-right: 16px;
`
const QuickMenuContainer = styled(Flex)`
  padding: 16px 20px;
`
const MenuLinkContainer = styled.div`
  border-left: solid 1px ${theme.colors.grayLight};
  padding-left: 16px;
  flex: 1 0 auto;
`
const MenuLink = styled.a`
  ${theme.typography.rsubtitle}
  display: flex;
  padding: 8px;
  margin: 2px;
  align-items: center;
  color: ${theme.colors.black};
  border-radius: 4px;
  cursor: pointer;

  :hover {
    background-color: ${theme.colors.grayLighter};
  }

  > * {
    &:nth-child(1) {
      margin-right: 12px;
    }
  }
`
const Divider = styled.div`
  border-bottom: solid 1px ${theme.colors.grayLight};
  margin: 12px 0;
`
const StyledSkeleton = styled(Skeleton)`
  display: inline-block;
  margin: ${(props) => props.margin};
  border-radius: ${(props) => props.borderRadius};
`

const NameWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 8px;
`

const ProfileButton = styled.a`
  ${theme.typography.blabel};
  cursor: pointer;
  padding: 8px;
  border: 1px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  border-radius: ${theme.radius.lg};
`

function Profile({
  name,
  imageProfile,
  isLoading,
  isError,
  handleOpenRalaliPlusModal,
  pinStatus,
  isHandphoneVerified,
  isFeatureSetAndChangePinEnable,
}: {
  name: string
  imageProfile: string
  isLoading: boolean
  isError: boolean
  handleOpenRalaliPlusModal: Function
  pinStatus?: string
  isHandphoneVerified?: boolean
  isFeatureSetAndChangePinEnable?: boolean
}): JSX.Element {
  const { postLogout } = useStoreActions<IStoreModel>(
    (actions) => actions.logOut,
  )
  const profileQuickLink = [
    {
      icon: <OrderIcon />,
      menu: 'Pembelian',
      target: '_blank',
      link: `${WEB_DESKTOP_RALALI_URL}/customer/order-management`,
    },
    {
      icon: <InvoiceIcon />,
      menu: 'Tagihan',
      target: '_blank',
      link: `${WEB_DESKTOP_RALALI_URL}/billing/order-history`,
    },
    {
      icon: <TenderIcon />,
      menu: 'Ralali Tender',
      target: '_blank',
      link: `${WEB_DESKTOP_RALALI_URL}/permintaan-penawaran/history-responded`,
    },
    {
      icon: <RefundIcon />,
      menu: 'Refund',
      target: '_blank',
      link: `${WEB_DESKTOP_RALALI_URL}/refund`,
    },
    {
      icon: <ReviewIcon />,
      menu: 'Ulasan Pesanan',
      target: '_blank',
      link: `${WEB_DESKTOP_RALALI_URL}/customer/testimonial`,
    },
  ]

  const [isLoadedMenuCredit, setIsLoadedMenuCredit] = useState(false)

  const globalRalaliPlusShowBalance = useLoadedFlag(
    'biz--ralali-plus--enabled--global',
  )

  const isEnablePartnerCurencyCode = useLoadedFlag(
    'rel--ralali-plus--currency-code-partner--website',
  )

  useEffect(() => {
    if (globalRalaliPlusShowBalance) {
      setIsLoadedMenuCredit(true)
    }
  }, [globalRalaliPlusShowBalance])

  return (
    <StyledProfile data-testid="profile-component">
      <ProfilePictureContainer alignItems="center">
        {imageProfile !== undefined && !isLoading && !isError && (
          <Image
            src={cdn({
              path: `/assets/img/profile/${imageProfile}`,
            })}
            objectFit="cover"
            radius="50%"
            width="50px"
            height="50px"
          />
        )}
        {(isLoading || isError) && (
          <StyledSkeleton
            height="50px"
            width="50px"
            borderRadius="50%"
            margin="0"
          />
        )}
        {(isLoading || isError) && (
          <StyledSkeleton
            height="22px"
            width="300px"
            borderRadius="10px"
            margin="0 0 0 10px"
          />
        )}
        {!isLoading && !isError && (
          <NameWrapper>
            <ProfileLink
              href={`${WEB_DESKTOP_RALALI_URL}/customer/profile`}
            >
              {name}
            </ProfileLink>
            <ProfileButton href="/customer/profile">
              Lihat Profil
            </ProfileButton>
          </NameWrapper>
        )}
      </ProfilePictureContainer>
      <QuickMenuContainer>
        <>
          <ProfileOverviewContainer>
            {isFeatureSetAndChangePinEnable &&
              isHandphoneVerified && (
                <MenuPin pinStatus={pinStatus} />
              )}
            <MenuWallet />
            {globalRalaliPlusShowBalance &&
              isLoadedMenuCredit &&
              (isEnablePartnerCurencyCode ? (
                <MenuCreditV2
                  onClickMenuLink={handleOpenRalaliPlusModal}
                />
              ) : (
                <MenuCredit
                  onClickMenuLink={handleOpenRalaliPlusModal}
                />
              ))}
            <MenuPoints />
            <MenuVoucher />
            <Divider />
            <MenuLink onClick={postLogout}>
              <LogoutIcon />
              <span>Keluar</span>
            </MenuLink>
          </ProfileOverviewContainer>
          <MenuLinkContainer>
            {profileQuickLink.map((link) => (
              <MenuLink
                target={link.target}
                href={link.link}
                key={link.menu}
              >
                {link.icon}
                <span>{link.menu}</span>
              </MenuLink>
            ))}
          </MenuLinkContainer>
        </>
      </QuickMenuContainer>
    </StyledProfile>
  )
}

export default Profile
